import Cookies from "js-cookie";
import produce from "immer";
import _memoize from "lodash/memoize";
import { useSelector } from "react-redux";
import { TableIdentifier } from "../../store/tableData/types";
import { Dictionary } from "../../globalTypes";
import { RootState } from "../../store/types";
import { getDefaultMaxPageRowCount } from "../../store/user/selectors";

export type RowsPerPageDict = {
  [TI in TableIdentifier as TI["kind"]]: Dictionary<TI["idWithinKind"], number>;
};
export const RPP_COOKIE_NAME = "rowsPerPage";

export function getCookieDefaultTemplateRowsPerPage(
  identifier: TableIdentifier
): number | null {
  const rppCookie = Cookies.get(RPP_COOKIE_NAME);
  const getRppDict = _memoize((cookie) => JSON.parse(cookie || "{}"));
  const rppDict: RowsPerPageDict = getRppDict(rppCookie);

  const rppForKind: Dictionary<string, number> = rppDict[identifier.kind] ?? {};
  const defaultRppForTemplateId = rppForKind[identifier.idWithinKind];

  return defaultRppForTemplateId ?? null;
}

export function setCookieDefaultTemplateRowsPerPage(
  identifier: TableIdentifier,
  rowsPerPage: number
) {
  const rppCookie = Cookies.get(RPP_COOKIE_NAME);
  const rppDict: RowsPerPageDict = JSON.parse(rppCookie || "{}");

  const newRppDict = produce(rppDict, (draft) => {
    if (!draft[identifier.kind]) draft[identifier.kind] = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (draft[identifier.kind] as any)[identifier.idWithinKind] = rowsPerPage; // Cast bohužel nutnej - https://stackoverflow.com/questions/65327902/derive-typescript-interface-from-discriminiated-union?noredirect=1#comment115509210_65328373
  });

  const newRppDictSerialized = JSON.stringify(newRppDict);

  Cookies.set("rowsPerPage", newRppDictSerialized);
}

export function getDefaultTableRowsPerPage(
  identifier: TableIdentifier,
  state: RootState
) {
  const cookieDefault = getCookieDefaultTemplateRowsPerPage(identifier);
  const userGlobalDefault = getDefaultMaxPageRowCount(state);

  if (cookieDefault) return cookieDefault;
  if (userGlobalDefault) return userGlobalDefault;
  return null;
}

export function useDefaultTableRowsPerPage(identifier: TableIdentifier) {
  const store = useSelector((state) => state);
  return getDefaultTableRowsPerPage(identifier, store);
}
